// Percorso file: src/pages/ClinicBoard.jsx
//
// LA BACHECA DI UN GRUPPO CLINIC, «BOARD - CHAT» — cantiere CLINICS /
// OFFSEASON, Fetta A (26/9/2026).
//
// Due cose in un file:
//   · `BachecaClinic`, il componente della bacheca (elenco, compositore,
//     «Modify», «Delete», aggiornamento a intervalli), usato da due porte;
//   · la PAGINA del socio e dell'ospite, /clinics/board/:eventId/:personId, a
//     cui si arriva dal bottone «BOARD - CHAT» delle righe ENROLLED di /clinics
//     (ClinicMember.jsx). La plancia (ClinicEventDetail.jsx) monta lo stesso
//     componente sotto le sessioni di ogni gruppo, con le rotte admin.
//
// ⚠️ UNA PERSONA ALLA VOLTA, CON L'ID NELL'INDIRIZZO, come il calendario: il
// gruppo NON lo sa la pagina, lo risolve il server dall'iscrizione della
// persona in quell'evento. Nessun `groupId` arriva al socio.
//
// ⚙️ LA VISTA DEL COACH (Fetta C, BAC-2/BAC-22): con `?group=<id>` nell'indirizzo
// (e la propria scheda come persona) il COACH DELL'EVENTO apre la bacheca di
// un gruppo qualsiasi del suo evento; il gruppo viaggia verso il server nella
// stessa forma, e il server decide da sé se concederlo. La pagina lo dice a
// schermo («Coach view»).
//
// ⚠️ IL PERMESSO NON SI VALUTA QUI. Lo decide il server: chi legge (sé stessi,
// o il tutore registrato in `guardians`), a nome di chi si scrive (`subjects`,
// dal server), chi modifica (solo l'autore, entro 5 minuti) e chi cancella.
// La pagina accende i comandi sui dati che il server manda (`mine`) e non
// decide nulla: un gesto non ammesso torna col suo rifiuto.
//
// ⚠️ NIENTE TEMPO REALE (BAC-14): la bacheca vive dietro il muro del mondo
// clinic (default-deny) e si legge SOLO dal backend. Mentre è aperta si rilegge
// ogni `INTERVALLO_MS`; il timer si ferma quando la vista si chiude o la
// scheda va in background, e riparte (con una lettura subito) al ritorno.
//
// ⚠️ SOLO TESTO: niente allegati, niente reazioni, niente risposte citate,
// niente spunte di lettura. La veste è quella di ClinicEvents.css (classi
// `qpsc-clinic__board-*`): Messaging.css NON si importa, per non legare la
// pagina clinic al foglio della Live Chat.
//
// Testi a schermo in inglese (CLAUDE.md §3.7).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FaComments } from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { messaggioDelServer } from "../utils/enrolDati";
import "../components/AdminTools.css";
import "./ClinicEvents.css";

// 🔵 La frequenza dell'aggiornamento: 20 secondi (da confermare al collaudo).
const INTERVALLO_MS = 20000;

// La finestra di «Modify»: il valore del server (CLINIC_BACHECA_MODIFICA_MINUTI,
// che è quello della Live Chat). Qui decide solo se MOSTRARE il comando.
const FINESTRA_MODIFICA_MS = 5 * 60 * 1000;

// Lo stesso tetto del server (CLINIC_BACHECA_TESTO_MAX): il campo non lascia
// scrivere oltre, e il server comunque rifiuterebbe.
const TESTO_MAX = 2000;

function quandoLeggibile(iso) {
  const data = typeof iso === "string" ? new Date(iso) : null;

  if (!data || Number.isNaN(data.getTime())) return "";

  return data.toLocaleString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function entroLaFinestra(iso) {
  const ms = typeof iso === "string" ? Date.parse(iso) : NaN;

  return Number.isFinite(ms) && Date.now() - ms <= FINESTRA_MODIFICA_MS;
}

function spiegazione(err, ripiego) {
  const dalServer = messaggioDelServer(err);

  if (dalServer) return dalServer;

  return err && err.status ? ripiego : "The club server could not be reached.";
}

/**
 * LA BACHECA.
 *
 * @param {string} percorso      rotta di lettura e di scrittura (GET e POST)
 * @param {function} percorsoMessaggio  id → rotta del singolo messaggio (PATCH, DELETE)
 * @param {boolean} amministrazione  vista della plancia: scrive a proprio nome,
 *   «Delete» su ogni messaggio
 * @param {boolean} scritturaAperta  (plancia) evento aperto: si scrive e si modifica
 * @param {string} idPreferito   (socio) il soggetto proposto per primo nel selettore
 * @param {string} nomeIo        il proprio nome proprio, per «You» / «You · as …»
 * @param {function} onDati      riceve la risposta di lettura (titolo, gruppo)
 */
export function BachecaClinic({
  percorso,
  percorsoMessaggio,
  amministrazione = false,
  scritturaAperta = true,
  idPreferito = "",
  nomeIo = "",
  onDati,
}) {
  // "caricamento" | "ok" | "negato" | "assente" | "errore"
  const [stato, setStato] = useState("caricamento");
  const [dati, setDati] = useState(null);
  const [aggiornamentoFallito, setAggiornamentoFallito] = useState(false);

  const [testo, setTesto] = useState("");
  const [soggettoScelto, setSoggettoScelto] = useState("");
  const [inCorso, setInCorso] = useState(null);
  const [erroreGesto, setErroreGesto] = useState(null);
  const [modifica, setModifica] = useState(null);

  // `onDati` può cambiare a ogni giro del genitore: lo si legge da un ref,
  // così la lettura non riparte per questo.
  const onDatiRef = useRef(onDati);
  onDatiRef.current = onDati;

  const vivoRef = useRef(true);

  const leggi = useCallback(async () => {
    try {
      const risposta = await api.get(percorso);

      if (!vivoRef.current) return;

      setDati(risposta || {});
      setStato("ok");
      setAggiornamentoFallito(false);
      if (onDatiRef.current) onDatiRef.current(risposta || {});
    } catch (err) {
      if (!vivoRef.current) return;

      // Un 403 o un 404 anche a bacheca già aperta vuol dire che non la si
      // vede più (evento chiuso, persona uscita dal gruppo): sparisce (BAC-6).
      if (err && err.status === 403) {
        setStato("negato");
      } else if (err && err.status === 404) {
        setStato("assente");
      } else {
        setStato((prima) => (prima === "ok" ? prima : "errore"));
        setAggiornamentoFallito(true);
      }
    }
  }, [percorso]);

  // L'AGGIORNAMENTO A INTERVALLI: una lettura subito, poi ogni INTERVALLO_MS
  // finché la scheda è in primo piano. In background il timer si ferma; al
  // ritorno si rilegge subito e riparte.
  useEffect(() => {
    vivoRef.current = true;
    let timer = null;

    const avvia = () => {
      if (!timer) timer = setInterval(leggi, INTERVALLO_MS);
    };
    const ferma = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const cambioVisibilita = () => {
      if (document.visibilityState === "visible") {
        leggi();
        avvia();
      } else {
        ferma();
      }
    };

    setStato("caricamento");
    setDati(null);
    leggi();
    if (document.visibilityState === "visible") avvia();
    document.addEventListener("visibilitychange", cambioVisibilita);

    return () => {
      vivoRef.current = false;
      ferma();
      document.removeEventListener("visibilitychange", cambioVisibilita);
    };
  }, [leggi]);

  const messaggi = dati && Array.isArray(dati.messages) ? dati.messages : [];
  const omessi = dati && Number.isFinite(dati.omitted) ? dati.omitted : 0;
  const soggetti = !amministrazione && dati && Array.isArray(dati.subjects) ? dati.subjects : [];

  // Il soggetto corrente: quello scelto se è ancora fra le possibilità;
  // altrimenti la persona della bacheca; altrimenti la prima.
  const idsSoggetti = soggetti.map((s) => String(s.id));
  const soggettoCorrente = idsSoggetti.includes(soggettoScelto)
    ? soggettoScelto
    : idsSoggetti.includes(String(idPreferito))
    ? String(idPreferito)
    : idsSoggetti[0] || "";
  const voceIo = soggetti.find((s) => s.self);
  const nomeProprio = (voceIo && voceIo.firstName) || nomeIo;

  const puoScrivere = amministrazione ? scritturaAperta : soggetti.length > 0;

  const invia = async () => {
    const pulito = testo.trim();

    if (!pulito || inCorso) return;

    setInCorso("invio");
    setErroreGesto(null);

    const corpo = { testo: pulito };
    const scelto = soggetti.find((s) => String(s.id) === soggettoCorrente);

    // Si dichiara il soggetto SOLO se è un figlio: per sé stessi non serve.
    if (scelto && !scelto.self) corpo.soggettoDichiarato = String(scelto.id);

    try {
      await api.post(percorso, corpo);
      setTesto("");
      await leggi();
    } catch (err) {
      setErroreGesto(spiegazione(err, "The message could not be sent."));
    } finally {
      setInCorso(null);
    }
  };

  const salvaModifica = async () => {
    if (!modifica || inCorso) return;

    const pulito = modifica.testo.trim();

    if (!pulito) return;

    setInCorso(`modifica-${modifica.id}`);
    setErroreGesto(null);

    try {
      await api.patch(percorsoMessaggio(modifica.id), { testo: pulito });
      setModifica(null);
      await leggi();
    } catch (err) {
      setErroreGesto(spiegazione(err, "The message could not be changed."));
    } finally {
      setInCorso(null);
    }
  };

  const cancella = async (messaggio) => {
    if (inCorso) return;
    if (!window.confirm("Delete this message? It cannot be recovered.")) return;

    setInCorso(`cancella-${messaggio.id}`);
    setErroreGesto(null);

    try {
      await api.del(percorsoMessaggio(messaggio.id));
      if (modifica && modifica.id === messaggio.id) setModifica(null);
      await leggi();
    } catch (err) {
      setErroreGesto(spiegazione(err, "The message could not be deleted."));
    } finally {
      setInCorso(null);
    }
  };

  if (stato === "caricamento") {
    return <p className="qpsc-clinic__hint">Loading…</p>;
  }

  if (stato === "negato" || stato === "assente") {
    return (
      <p className="qpsc-clinic__error" role="alert">
        This board is not available.
      </p>
    );
  }

  if (stato === "errore") {
    return (
      <p className="qpsc-clinic__error" role="alert">
        The board could not be loaded. Reload the page to try again.
      </p>
    );
  }

  // Senza gruppo, nessuna bacheca (BAC-11).
  if (!amministrazione && dati && dati.board === false) {
    return (
      <p className="qpsc-clinic__hint">
        Not in a group yet: the board appears once the club assigns one.
      </p>
    );
  }

  return (
    <div className="qpsc-clinic__board">
      {omessi > 0 && (
        <p className="qpsc-clinic__hint">Older messages are not shown.</p>
      )}

      {messaggi.length === 0 ? (
        <p className="qpsc-clinic__hint">No messages yet.</p>
      ) : (
        <ul className="qpsc-clinic__board-list">
          {messaggi.map((m) => {
            const inModifica = modifica && modifica.id === m.id;
            const puoModificare =
              m.mine && puoScrivere && entroLaFinestra(m.quando);
            const puoCancellare = m.mine || amministrazione;
            // Sul proprio messaggio: «You», oppure «You · as ‹nome›» se è stato
            // scritto a nome di un figlio (come nella Live Chat). Il server
            // non manda il soggetto: lo si riconosce dal nome, che per sé
            // stessi è il proprio (o il proprio col ruolo, per l'admin).
            const perSeStessi =
              !m.nomeMostrato ||
              m.nomeMostrato === nomeProprio ||
              (nomeProprio && m.nomeMostrato.startsWith(`${nomeProprio} (`));
            const autore = m.mine
              ? perSeStessi
                ? "You"
                : `You · as ${m.nomeMostrato}`
              : m.nomeMostrato || "Member";

            return (
              <li key={m.id} className="qpsc-clinic__board-msg">
                <div className="qpsc-clinic__board-meta">
                  <span className="qpsc-clinic__board-author">{autore}</span>
                  <span>{quandoLeggibile(m.quando)}</span>
                  {m.modificato && <span>edited</span>}
                </div>

                {inModifica ? (
                  <>
                    <textarea
                      className="qpsc-input qpsc-clinic__textarea"
                      value={modifica.testo}
                      maxLength={TESTO_MAX}
                      aria-label="Modify message"
                      onChange={(e) => setModifica({ ...modifica, testo: e.target.value })}
                    />
                    <div className="qpsc-clinic__dates-add">
                      <button
                        type="button"
                        className="qpsc-ghost-btn"
                        onClick={salvaModifica}
                        disabled={inCorso !== null || !modifica.testo.trim()}
                      >
                        <span>{inCorso === `modifica-${m.id}` ? "Saving…" : "Save"}</span>
                      </button>
                      <button
                        type="button"
                        className="qpsc-ghost-btn"
                        onClick={() => setModifica(null)}
                        disabled={inCorso !== null}
                      >
                        <span>Discard changes</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="qpsc-clinic__board-text">{m.testo}</p>
                )}

                {!inModifica && (puoModificare || puoCancellare) && (
                  <div className="qpsc-clinic__board-acts">
                    {puoModificare && (
                      <button
                        type="button"
                        className="qpsc-clinic__session-act"
                        onClick={() => setModifica({ id: m.id, testo: m.testo })}
                        disabled={inCorso !== null}
                      >
                        Modify
                      </button>
                    )}
                    {puoCancellare && (
                      <button
                        type="button"
                        className="qpsc-clinic__session-act qpsc-clinic__session-act--danger"
                        onClick={() => cancella(m)}
                        disabled={inCorso !== null}
                      >
                        {inCorso === `cancella-${m.id}` ? "Deleting…" : "Delete"}
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {aggiornamentoFallito && (
        <p className="qpsc-clinic__hint">
          The board could not be refreshed. It will try again shortly.
        </p>
      )}

      {erroreGesto && (
        <p className="qpsc-clinic__error" role="alert">
          {erroreGesto}
        </p>
      )}

      {puoScrivere ? (
        <div className="qpsc-clinic__board-compose">
          {/* IL SELETTORE «a nome di chi» — compare SOLO con più di una
              possibilità (BAC-10), come nella Live Chat. */}
          {soggetti.length > 1 && (
            <select
              className="qpsc-select"
              aria-label="Write on behalf of"
              value={soggettoCorrente}
              onChange={(e) => setSoggettoScelto(e.target.value)}
            >
              {soggetti.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.self ? (s.coach ? "as coach" : "as yourself") : `as ${s.firstName}`}
                </option>
              ))}
            </select>
          )}

          <textarea
            className="qpsc-input qpsc-clinic__textarea"
            value={testo}
            maxLength={TESTO_MAX}
            placeholder="Write a message…"
            aria-label="Write a message"
            onChange={(e) => setTesto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                invia();
              }
            }}
          />

          <button
            type="button"
            className="qpsc-ghost-btn"
            onClick={invia}
            disabled={inCorso !== null || !testo.trim()}
          >
            <span>{inCorso === "invio" ? "Sending…" : "Send"}</span>
          </button>
        </div>
      ) : (
        <p className="qpsc-clinic__hint">
          {amministrazione
            ? "The event is closed: its boards are read-only."
            : "You cannot write on this board."}
        </p>
      )}
    </div>
  );
}

const TESTO_SEZIONE = "Clinic / Off Season / Specials";

export default function ClinicBoard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { eventId, personId } = useParams();
  const [ricerca] = useSearchParams();
  const idEvento = String(eventId || "").trim();
  const idPersona = String(personId || "").trim();
  // (Fetta C) Il gruppo chiesto dalla vista del coach; "" = la bacheca della
  // persona, come prima.
  const idGruppoCoach = String(ricerca.get("group") || "").trim();

  const [intestazione, setIntestazione] = useState(null);

  const base = `/clinic-member/board/${encodeURIComponent(idEvento)}/${encodeURIComponent(idPersona)}`;
  const coda = idGruppoCoach ? `?group=${encodeURIComponent(idGruppoCoach)}` : "";

  const riceviDati = useCallback((d) => {
    setIntestazione({
      titolo: d && typeof d.eventTitle === "string" ? d.eventTitle : "",
      gruppo: d && typeof d.groupName === "string" ? d.groupName : "",
      coach: Boolean(d && d.coach === true),
    });
  }, []);

  return (
    <HomeShell backTo={() => navigate(-1)} showSwitch={false}>
      <section className="qpsc-clinic">
        <header className="qpsc-clinic__head">
          <div className="qpsc-clinic__title">
            <FaComments className="qpsc-clinic__title-icon" aria-hidden="true" />
            <h1 className="qpsc-page-title">BOARD - CHAT</h1>
          </div>
        </header>

        <p className="qpsc-clinic__board-context">
          {intestazione && intestazione.titolo
            ? [intestazione.titolo, intestazione.gruppo].filter(Boolean).join(" · ")
            : TESTO_SEZIONE}
        </p>

        {intestazione && intestazione.coach && (
          <p className="qpsc-clinic__hint">
            Coach view: you can read and write on every group board of this event.
            You can modify or delete only your own messages.
          </p>
        )}

        <BachecaClinic
          percorso={`${base}${coda}`}
          percorsoMessaggio={(id) => `${base}/messages/${encodeURIComponent(id)}${coda}`}
          idPreferito={idPersona}
          nomeIo={user && typeof user.firstName === "string" ? user.firstName.trim() : ""}
          onDati={riceviDati}
        />
      </section>
    </HomeShell>
  );
}
